"use client"

import { CreditsTag, MiniJobCard } from "@/components"
import { AdditionalProjectsCard, ProjectPreviewCard } from "@/components/JobApplication"
import { Avatar, Image, Separator, Text, TextArea } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import {
  ApplyToJobContentCreativeFragmentDoc,
  JobApplicationProjectPreviewFragmentDoc,
  JobPostingPayType,
  JobsJobApplicationProposedPayTypeChoices,
} from "@/gql/graphql"
import type { NovaSession } from "@/types"
import clsx from "clsx"
import Link from "next/link"
import { useMemo } from "react"
import { ApplyToJobContentSkeleton } from "../ApplyToJob/ApplyToJobContentSkeleton"
import { formatCurrencyLocale } from "@/utils/formatCurrency"

const JobApplicationContentJobPostingFragment = graphql(`
  fragment JobApplicationContentJobPosting on JobPosting {
    __typename
    id
    ...MiniJobCard
    position {
      name
      id
    }
    activeInteractionForCreative(creativeId: $creativeId) {
      ... on JobApplication {
        id
        coverLetter
        proposedPayString
        creativeWorkAttachments {
          edges {
            node {
              id
              portfolioProject {
                ...JobApplicationProjectPreview
              }
            }
          }
        }
      }
    }
  }
`)

interface Props {
  onClose?: () => void
  loading: boolean
  creative: FragmentType<typeof ApplyToJobContentCreativeFragmentDoc> | undefined | null
  jobPosting: FragmentType<typeof JobApplicationContentJobPostingFragment> | undefined | null
  serverSession: NovaSession | null
  onSelectWork?: () => void
}

export const JobApplicationContent: React.FC<Props> = ({ loading, serverSession, ...props }) => {
  const jobPosting = useFragment(JobApplicationContentJobPostingFragment, props.jobPosting)
  const creative = useFragment(ApplyToJobContentCreativeFragmentDoc, props.creative)

  const jobApplication =
    jobPosting?.activeInteractionForCreative?.__typename === "JobApplication"
      ? jobPosting.activeInteractionForCreative
      : null
  const selectedWork = jobApplication?.creativeWorkAttachments.edges || []

  const selectedIds = useMemo(
    () =>
      selectedWork
        .map(sw => getFragmentData(JobApplicationProjectPreviewFragmentDoc, sw?.node?.portfolioProject)?.id)
        .filter((id): id is string => id !== undefined),
    [selectedWork]
  )

  if (loading) {
    return <ApplyToJobContentSkeleton />
  }

  return (
    <div className="mt-6 flex flex-col">
      <MiniJobCard jobPosting={jobPosting} />
      <div className="py-6">
        <Separator />
      </div>

      <div className="flex flex-row items-center gap-x-3">
        <Avatar size="lg" src={creative?.avatarUrl} initials={creative?.avatarInitials} />
        <div className="flex w-full flex-col gap-y-1">
          <Text>{creative?.name}</Text>

          <CreditsTag
            creditsCount={creative?.creditsCount.totalCount}
            positionName={jobPosting?.position?.name}
            textVariant="body3"
          />
        </div>
      </div>

      <>
        <div className="mt-5 flex w-[calc(100%+40px)] flex-row gap-x-2 overflow-x-auto pr-10">
          {selectedWork.map((project, index) => {
            return (
              <ProjectPreviewCard
                key={index}
                projectData={project?.node?.portfolioProject}
                positionId={jobPosting?.position?.id}
              />
            )
          })}

          <AdditionalProjectsCard creative={creative} selectedProjectsIds={selectedIds} />
        </div>
      </>

      <div className="mt-6 flex w-full flex-col">
        <Text variant="label">Proposed rate</Text>
        <TextArea
          disabled={true}
          rows={1}
          value={jobApplication?.proposedPayString || "N/A"}
          className={clsx("mt-2 w-full rounded-lg border border-bg-3 bg-bg-2 px-5")}
        />
      </div>
      <div className="mt-6 flex w-full flex-col" id="add-note-section">
        <Text variant="label">Your note</Text>
        <TextArea
          disabled={true}
          value={jobApplication?.coverLetter || ""}
          className={clsx("mt-2 w-full rounded-lg border border-bg-3 bg-bg-2 px-5")}
          placeholder="Attach any information that will help you stand out"
        />
      </div>
    </div>
  )
}
